import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getRole, getRoleProgress } from '../../services/userApi';
import { useSEO } from '../../hooks/useSEO';
import PageMeta from '../../components/common/PageMeta';
import { Helmet } from 'react-helmet-async';

// ── SVG Icons ──────────────────────────────────────────────────────────────────
function Svg({ children, size = 18, color, sw = '1.75' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color || 'currentColor'} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}
const ArrowLeft   = ({ size = 16 }) => <Svg size={size}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></Svg>;
const ChevronDown = ({ size = 18, open }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const CheckCircle = ({ size = 16 }) => <Svg size={size} color="#059669"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Svg>;
const Lock        = ({ size = 14 }) => <Svg size={size} sw="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></Svg>;
const Play        = ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const Retry       = ({ size = 12 }) => <Svg size={size} sw="2.5"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></Svg>;
const QuestionMark = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const AiIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);
const ShareIcon = () => (
  <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const PERF = (pct) => {
  if (pct >= 85) return { label: 'Excellent', color: '#059669', bg: '#D1FAE5' };
  if (pct >= 65) return { label: 'Good',      color: '#2563EB', bg: '#EFF6FF' };
  if (pct >= 45) return { label: 'Fair',       color: '#D97706', bg: '#FEF3C7' };
  return { label: 'Needs work',               color: '#C1272D', bg: '#FEE2E2' };
};

const CHAPTER_COLORS = [
  '#1B3A5C','#C1272D','#7C3AED','#2563EB','#059669',
  '#D97706','#C1272D','#0d2238','#2A5280','#64748B',
];

export default function RoleOverviewPage() {
  const { slug } = useParams();
  const { isAuthenticated, hasActiveSubscription, refreshSubscriptions } = useAuth();
  const navigate = useNavigate();

  const [role,     setRole]     = useState(null);
  const [chapters, setChapters] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const subRefresh = isAuthenticated ? refreshSubscriptions() : Promise.resolve();
    Promise.all([
      getRole(slug).then(r => { setRole(r.data.role); setChapters(r.data.chapters); }),
      isAuthenticated ? getRoleProgress(slug).then(r => setProgress(r.data)).catch(() => {}) : Promise.resolve(),
      subRefresh,
    ]).catch(() => navigate('/roles')).finally(() => setLoading(false));
  }, [slug, isAuthenticated]); // eslint-disable-line

  const seoTitle       = role ? `${role.title} Interview Questions: 1,000 Q&A with AI Feedback` : 'CentricQ';
  const seoDescription = role
    ? `Practice ${role.title} interview questions with real-time AI feedback on every answer. 1,000 questions — MCQ, written & spoken. 200 questions free, no credit card needed.`
    : '';

  const chapterTitles = chapters.map(c => c.title);

  const jsonLd = role ? JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: `${role.title} Interview Preparation`,
      description: `Practice ${role.title} interview questions with real-time AI feedback. 1,000 questions across 10 chapters — MCQ, written, and spoken practice.`,
      provider: { '@type': 'Organization', name: 'CentricQ', url: 'https://centricq.com' },
      url: `https://centricq.com/roles/${slug}`,
      educationalLevel: 'Professional',
      about: { '@type': 'Thing', name: `${role.title} job interviews` },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: 'PT5H',
        instructor: { '@type': 'Organization', name: 'CentricQ AI' },
      },
      offers: [
        { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: '200 questions free' },
        { '@type': 'Offer', price: '10.00', priceCurrency: 'USD', priceSpecification: { billingDuration: 'P1M' } },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',         item: 'https://centricq.com' },
        { '@type': 'ListItem', position: 2, name: 'Browse Roles', item: 'https://centricq.com/roles' },
        { '@type': 'ListItem', position: 3, name: `${role.title} Interview Questions`, item: `https://centricq.com/roles/${slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: `What ${role.title} interview questions should I prepare for?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `CentricQ covers ${chapterTitles.length} key areas for ${role.title} interviews: ${chapterTitles.join(', ')}. Each area has 100 questions with AI-evaluated feedback.`,
          },
        },
        {
          '@type': 'Question',
          name: `How many ${role.title} interview questions are there?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: `CentricQ has 1,000 ${role.title} interview questions across 10 chapters, covering multiple choice, written answer, and spoken answer formats. 200 questions are free.`,
          },
        },
        {
          '@type': 'Question',
          name: `How do I practice for a ${role.title} interview?`,
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'CentricQ provides 3 answer formats to simulate real interviews: multiple choice for quick knowledge checks, written answers for in-depth responses, and spoken answers to practice verbal delivery. Every answer is evaluated by Claude AI.',
          },
        },
      ],
    },
  ]) : null;

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #1B3A5C', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!role) return null;

  const hasSubscription = isAuthenticated && hasActiveSubscription(role.id);
  const completedSC     = Object.values(progress).filter(p => p.completed).length;
  const totalSC         = chapters.reduce((s, c) => s + (c.sub_chapters?.length || 0), 0);
  const overallPct      = totalSC > 0 ? Math.round((completedSC / totalSC) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <PageMeta
        title={seoTitle}
        description={seoDescription}
        path={`/roles/${slug}`}
      />
      <Helmet>
        <link rel="preload" as="style" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" />
      </Helmet>
      {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />}
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        .ro-card{animation:fadeUp 0.35s ease both}
        @media(prefers-reduced-motion:reduce){.ro-card{animation:none}}
      `}</style>

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={{ background: '#0F172A', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50 }}>
        <Link to={isAuthenticated ? '/dashboard' : '/roles'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'rgba(255,255,255,0.5)', transition: 'color 0.12s', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,0.9)'}
          onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.5)'}>
          <ArrowLeft size={16} />
          <span style={{ fontSize: 14 }}>{isAuthenticated ? 'Dashboard' : 'Browse roles'}</span>
        </Link>
        <Link to="/" style={{ fontSize: 17, fontWeight: 700, color: '#fff', textDecoration: 'none', letterSpacing: '-0.3px' }}>
          Centric<span style={{ color: '#C1272D' }}>Q</span>
        </Link>
        <button
          onClick={async () => {
            const url = `https://centricq.com/roles/${slug}`;
            const title = role ? `${role.title} Interview Questions — CentricQ` : 'CentricQ';
            if (navigator.share) {
              try { await navigator.share({ title, url }); } catch {}
            } else {
              try { await navigator.clipboard.writeText(url); toast.success('Link copied!'); } catch { toast.error('Could not copy link'); }
            }
          }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s', fontFamily: "'Inter', sans-serif" }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
        >
          <ShareIcon /> Share
        </button>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(160deg, #0a1929 0%, #0d2238 50%, #1B3A5C 100%)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize: '56px 56px' }} />
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 384, pointerEvents: 'none', background: 'radial-gradient(ellipse at right center, rgba(193,39,45,0.12) 0%, transparent 65%)' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 900, margin: '0 auto', padding: '40px 24px 48px' }}>
          {/* ── Breadcrumb nav ─────────────────────────────────────────────── */}
          <nav aria-label="Breadcrumb" style={{ marginBottom: 20 }}>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 6px' }}>
              <li>
                <Link to="/" style={{ fontSize: 13, color: 'rgba(255,255,255,0.68)', textDecoration: 'none', transition: 'color 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.68)'}>
                  Home
                </Link>
              </li>
              <li style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>›</li>
              <li>
                <Link to="/roles" style={{ fontSize: 13, color: 'rgba(255,255,255,0.68)', textDecoration: 'none', transition: 'color 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.color='#fff'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.68)'}>
                  Browse Roles
                </Link>
              </li>
              <li style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>›</li>
              <li>
                <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  {role.title} Interview Questions
                </span>
              </li>
            </ol>
          </nav>

          {/* Category pill */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 999, padding: '6px 14px', marginBottom: 16, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#C1272D', flexShrink: 0 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>{role.category}</span>
          </div>

          <h1 style={{ margin: '0 0 12px', fontSize: 40, fontWeight: 800, color: '#fff', letterSpacing: '-0.8px', lineHeight: 1.1 }}>
            {role.title} Interview Questions
          </h1>

          {role.description && (
            <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.75, maxWidth: 580, color: 'rgba(255,255,255,0.6)' }}>
              {role.description}
            </p>
          )}

          {/* Meta stats */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 24 }}>
            {[
              { icon: <QuestionMark />, label: '1,000 questions' },
              { icon: null,             label: '10 chapters'     },
              { icon: <AiIcon />,       label: 'AI feedback',   accent: true },
            ].map(({ icon, label, accent }) => (
              <span key={label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, padding: '6px 14px', borderRadius: 999,
                background: accent ? 'rgba(193,39,45,0.2)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${accent ? 'rgba(193,39,45,0.3)' : 'rgba(255,255,255,0.12)'}`,
                color: 'rgba(255,255,255,0.75)',
              }}>
                {icon}{label}
              </span>
            ))}
          </div>

          {/* Progress bar — shown if user has started */}
          {isAuthenticated && completedSC > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, maxWidth: 380 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                  You've completed {overallPct}% of this role
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                  {completedSC}/{totalSC}
                </span>
              </div>
              <div style={{ maxWidth: 380, height: 8, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.12)' }}>
                <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${overallPct}%`, background: '#C1272D' }} />
              </div>
              {totalSC - completedSC > 0 && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                  Only {totalSC - completedSC} sub-chapter{totalSC - completedSC !== 1 ? 's' : ''} left to earn your certificate!
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Subscribe banner ─────────────────────────────────────────────────── */}
      {!hasSubscription && (
        <div style={{ background: '#fff', padding: '16px 24px', borderBottom: '1px solid #E2E8F0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
                200 free questions per role — no card required
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#64748B' }}>
                Subscribe from $10/month to unlock all 1,000 questions with unlimited AI feedback
              </p>
            </div>
            <Link to="/pricing"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#C1272D', color: '#fff', textDecoration: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, transition: 'background 0.15s', flexShrink: 0, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background='#A8202B'}
              onMouseLeave={e => e.currentTarget.style.background='#C1272D'}>
              Unlock access — $10/mo
            </Link>
          </div>
        </div>
      )}

      {/* ── Chapter list ─────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {chapters.map((chapter, chIdx) => {
            const isOpen           = expanded[chapter.id] !== false;
            const subChapters      = chapter.sub_chapters || [];
            const chapterCompleted = subChapters.filter(sc => progress[sc.id]?.completed).length;
            const chapterPct       = subChapters.length > 0
              ? Math.round((chapterCompleted / subChapters.length) * 100) : 0;
            const accentColor      = CHAPTER_COLORS[chIdx % CHAPTER_COLORS.length];
            const allDone          = chapterCompleted === subChapters.length && subChapters.length > 0;

            return (
              <div key={chapter.id} className="ro-card"
                style={{
                  background: '#FFFFFF', borderRadius: 12, overflow: 'hidden',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
                  animationDelay: `${chIdx * 40}ms`,
                }}>

                {/* Chapter header */}
                <button
                  onClick={() => setExpanded(e => ({ ...e, [chapter.id]: !isOpen }))}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  {/* Chapter number badge */}
                  <div style={{ width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#fff', fontSize: 14, flexShrink: 0, background: allDone ? '#059669' : accentColor }}>
                    {allDone
                      ? <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      : chapter.chapter_number}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600, color: '#0F172A' }}>{chapter.title}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 120, height: 5, borderRadius: 999, overflow: 'hidden', background: '#F1F5F9' }}>
                        <div style={{ height: '100%', borderRadius: 999, transition: 'width 0.7s ease', width: `${chapterPct}%`, background: allDone ? '#059669' : accentColor }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8' }}>
                        {chapterCompleted}/{subChapters.length}
                      </span>
                      {allDone && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#D1FAE5', color: '#059669' }}>
                          Complete
                        </span>
                      )}
                      {!allDone && chapterCompleted > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: accentColor }}>
                          {chapterPct}%
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronDown size={18} open={isOpen} />
                </button>

                {/* Sub-chapters */}
                {isOpen && subChapters.length > 0 && (
                  <div style={{ borderTop: '1px solid #E2E8F0' }}>
                    {subChapters.map((sc, scIdx) => {
                      const scp         = progress[sc.id];
                      const isCompleted = scp?.completed;
                      const isLocked    = !sc.is_free && !hasSubscription;
                      const pct         = isCompleted
                        ? Math.round((scp.score / scp.total_questions) * 100) : 0;
                      const perf        = isCompleted ? PERF(pct) : null;

                      return (
                        <div key={sc.id}
                          style={{
                            display: 'flex', alignItems: 'center', padding: '12px 20px 12px 76px',
                            borderBottom: scIdx < subChapters.length - 1 ? '1px solid #F8FAFC' : 'none',
                            opacity: isLocked ? 0.6 : 1,
                            transition: 'background 0.12s',
                            cursor: isLocked ? 'default' : 'default',
                          }}
                          onMouseEnter={e => { if (!isLocked) e.currentTarget.style.background='#F8FAFC'; }}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>

                          {/* Status indicator */}
                          <div style={{ flexShrink: 0, marginRight: 14 }}>
                            {isCompleted ? (
                              <CheckCircle size={18} />
                            ) : isLocked ? (
                              <div style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F1F5F9', color: '#64748B' }}>
                                <Lock size={10} />
                              </div>
                            ) : (
                              <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #CBD5E1' }} />
                            )}
                          </div>

                          {/* Title + free tag */}
                          <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span style={{ marginRight: 6, color: '#475569', fontWeight: 600 }}>{chapter.chapter_number}.{sc.sub_chapter_number}</span>
                              {sc.title}
                            </p>
                            {sc.is_free && !hasSubscription && (
                              <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', background: '#D1FAE5', color: '#059669' }}>
                                Free
                              </span>
                            )}
                          </div>

                          {/* Score + action */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            {isCompleted && perf && (
                              <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: perf.bg, color: perf.color }}>
                                {pct}%
                              </span>
                            )}
                            {isLocked ? (
                              <Link to="/pricing"
                                style={{ fontSize: 13, fontWeight: 600, textDecoration: 'none', color: '#C1272D', cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                                Unlock →
                              </Link>
                            ) : (
                              <Link to={`/study/${sc.id}`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 6,
                                  padding: '6px 14px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                                  textDecoration: 'none', transition: 'opacity 0.15s', cursor: 'pointer',
                                  ...(isCompleted
                                    ? { background: '#F1F5F9', color: '#334155' }
                                    : { background: accentColor, color: '#fff' }),
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity='0.82'}
                                onMouseLeave={e => e.currentTarget.style.opacity='1'}>
                                {isCompleted
                                  ? <><Retry size={11} /> Retry</>
                                  : <><Play  size={11} /> Start</>}
                              </Link>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Public SEO content section ─────────────────────────────────────── */}
      {/* Visible to ALL users (logged-in and public) — crawlable by Google */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px 56px' }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: '32px 36px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: '#0F172A', letterSpacing: '-0.3px' }}>
            About {role.title} Interview Preparation
          </h2>
          <p style={{ margin: '0 0 28px', fontSize: 15, lineHeight: 1.8, color: '#475569' }}>
            The {role.title} role demands a strong mix of technical knowledge and communication skills. Interviewers typically test core domain expertise, problem-solving ability, and how you communicate your reasoning. CentricQ helps you prepare systematically — covering every topic area with 1,000 questions across 10 chapters. You can practice multiple-choice questions for quick recall, written-answer questions to develop in-depth responses, and spoken-answer questions to rehearse your verbal delivery. Every answer is evaluated by Claude AI, giving you a score, specific feedback, and study tips in real time. 200 questions are free with no credit card required.
          </p>

          {chapters.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 600, color: '#0F172A' }}>
                What you&apos;ll cover
              </h3>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {chapters.slice(0, 3).map((chapter, i) => (
                  <li key={chapter.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#334155' }}>
                    <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, background: CHAPTER_COLORS[i % CHAPTER_COLORS.length], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#fff', marginTop: 1 }}>
                      {i + 1}
                    </span>
                    <span style={{ lineHeight: 1.6 }}>{chapter.title}</span>
                  </li>
                ))}
              </ul>
              {chapters.length > 3 && (
                <p style={{ margin: '14px 0 0', fontSize: 13, color: '#64748B' }}>
                  + {chapters.length - 3} more chapter{chapters.length - 3 !== 1 ? 's' : ''} inside
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
